import { Box, Center, Divider, Image, Text, VStack } from "@chakra-ui/react";
import { useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "src/assets/awaken/logos/logo-name.png";
import WhiteLogo from "src/assets/awaken/logos/white-logo.png";
import { NoNavBarPageTemplate } from "src/components/layouts/PageTemplate";

import { yupResolver } from "@hookform/resolvers/yup";
import { sendPasswordResetEmail } from "firebase/auth";
import { useForm } from "react-hook-form";
import { Button, Input } from "src/components";
import {
  DefaultErrors,
  failure,
  FailureOrSuccess,
  success,
  UnexpectedError,
} from "src/core";
import { useMyToast } from "src/hooks";
import { useTheme } from "src/hooks/useTheme";
import { colors, other } from "src/theme";
import { auth } from "src/utils/firebase";
import * as yup from "yup";

const schema = yup.object().shape({
  email: yup.string().email().required("Email is required.").nullable(),
});

type FormValues = {
  email: string;
};

const DEFAULT_VALUES: FormValues = {
  email: "",
};

export function ForgotPassword() {
  const [search] = useSearchParams();
  const toast = useMyToast();
  const navigate = useNavigate();
  const theme = useTheme();
  const title = search.get("title");
  const email = search.get("email");

  // Form hooks
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: { ...DEFAULT_VALUES, email: email || DEFAULT_VALUES.email },
  });

  const _logAuthError = ({ message }: { message: string }) => {
    toast.show({ message, status: "error" });
  };

  // Functions
  const _submitForgotPassword = useCallback(
    async (
      values: FormValues
    ): Promise<FailureOrSuccess<DefaultErrors, null>> => {
      try {
        await sendPasswordResetEmail(auth, values.email);

        toast.show({
          message:
            "Password reset email sent! If you don't see it, check your spam folder.",
          status: "success",
        });

        return success(null);
      } catch (err) {
        return failure(new UnexpectedError(err));
      }
    },
    []
  );

  const onSubmit = useCallback(
    async (values: FormValues) => {
      await _submitForgotPassword(values);
    },
    [_submitForgotPassword]
  );

  return (
    <NoNavBarPageTemplate>
      <VStack
        padding="2rem 0"
        flexDir="column"
        minH="100vh"
        display="flex"
        justifyContent="center"
      >
        <FreeTransactions />
        <Box
          display="flex"
          flexDir="column"
          maxWidth="30rem"
          margin="auto"
          padding="2rem"
          w="100%"
          borderRadius={other.borderRadius}
          bg={theme.background}
          border={`1px solid ${theme.border}`}
        >
          <form
            onSubmit={handleSubmit(onSubmit, (e) =>
              toast.show({
                status: "error",
                message: e.email?.message || "",
              })
            )}
          >
            <Link to="/">
              <Center>
                <Image
                  src={theme.theme === "light" ? Logo : WhiteLogo}
                  cursor="pointer"
                  w="10rem"
                  paddingTop="1rem"
                  paddingBottom="2rem"
                />
              </Center>
            </Link>

            <Box width="100%">
              <Input
                label="Email"
                isRequired
                autoComplete="email"
                control={control}
                name="email"
              />
            </Box>
            <br />

            <Button
              isLoading={isSubmitting}
              width="100%"
              variant="primary"
              type="submit"
              padding="1rem 2rem"
            >
              Send Password Reset Email
            </Button>

            <Divider margin="2rem auto" />

            <Text color={theme.text} margin="auto">
              Already have an account?&nbsp;
              <Box display="inline-block" textDecor="underline" color="black">
                <Link to="/login">
                  <Text color={colors.primary}>Log in</Text>
                </Link>
              </Box>
            </Text>
          </form>
        </Box>
      </VStack>
    </NoNavBarPageTemplate>
  );
}

const FreeTransactions = () => {
  const [search] = useSearchParams();
  const refCode = search.get("ref");

  if (!refCode || refCode === "undefined" || refCode === "null") return null;

  return (
    <Box
      w="100%"
      maxWidth="30rem"
      style={{
        color: colors.white,
        background: `linear-gradient(30deg, ${colors.green60} 0%, ${colors.green50} 50%, ${colors.green70} 100%)`,
        padding: "0.75rem 0.5rem",
        borderRadius: 5,
        fontWeight: "600",
        margin: "0.5rem auto",
        fontSize: 15,
        textAlign: "center",
      }}
    >
      You are unlocking <b>FREE</b> credit with code {refCode} 🔑
    </Box>
  );
};
